import React from "react";
import styled from "styled-components";
import { ResponsivePie } from "@nivo/pie";
import { CompassParagraph } from "../common/styledComponents";
import { ElementContainer, RowContainer } from "../containers/styledComponents";
import { LegendCircle } from "../visuals/styledComponents";
import { graphPaletteV2, greyText } from "../../styles";

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

const ChartContainer = styled.div<{ size: number }>`
  height: ${({ size }): number => size}px;
  width: ${({ size }): number => size}px;
`;

const ChartWrapper = styled.div<{ height: number }>`
  height: ${({ height }): number => height}px;
  display: flex;
  align-items: flex-start;
`;

const ScrollableLegend = styled(ElementContainer)`
  max-height: 120px;
  overflow-y: auto;
`;

// =============================================================================
// TYPES
// =============================================================================

export interface DonutChartData {
  label: string;
  value: number;
}

export interface CompactDonutChartProps {
  data: DonutChartData[];
  size?: number;
  colors?: string[];
  showLegend?: boolean;
  innerRadius?: number;
  /** Fixed container height for consistent sizing */
  containerHeight?: number;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const CompactDonutChart: React.FC<CompactDonutChartProps> = ({
  data,
  size = 120,
  colors = graphPaletteV2,
  showLegend = true,
  innerRadius = 0.6,
  containerHeight = 120,
}) => {
  const chartData = data.map((item, index) => ({
    id: item.label,
    label: item.label,
    value: item.value,
    color: colors[index % colors.length],
  }));

  return (
    <ChartWrapper height={containerHeight}>
      <RowContainer jc="flex-start" ai="flex-start" mt={0} mb={0}>
        <ChartContainer size={size}>
          <ResponsivePie
            data={chartData}
            margin={{ top: 5, right: 5, bottom: 5, left: 5 }}
            innerRadius={innerRadius}
            padAngle={2}
            cornerRadius={3}
            colors={colors}
            enableArcLinkLabels={false}
            enableArcLabels={false}
            isInteractive={true}
          />
        </ChartContainer>
        {showLegend && (
          <ScrollableLegend flexDirection="column" ml={16}>
            {data.map((item, index) => (
              <ElementContainer
                key={item.label}
                flexDirection="row"
                ai="center"
                mb={4}
              >
                <LegendCircle color={colors[index % colors.length]} />
                <CompassParagraph fontSize={11} ml={6} color={greyText}>
                  {item.label}: {item.value}
                </CompassParagraph>
              </ElementContainer>
            ))}
          </ScrollableLegend>
        )}
      </RowContainer>
    </ChartWrapper>
  );
};
